/* DELLUMIA | Product accordion information popup v4.5 — bounded DE LUXE measurement and stationary close
 * Functionality only: four explicitly marked product accordions.
 * The existing BOGOS / DE LUXE, Size Guide and product components are untouched.
 * Loads static Shopify page/policy content. It does not execute remote scripts/forms.
 */
(() => {
  'use strict';
  if (window.__dellumiaProductInfoPopupLoaded) return;
  window.__dellumiaProductInfoPopupLoaded = true;

  const LINK_SELECTOR = 'product-info [data-dellumia-info-popup-accordion] .accordion__content a[href]';
  const BLOCK_SELECTOR = '[data-dellumia-bogos-progress]';
  const MODAL_CLASS = 'dellumia-product-info-popup';
  const NON_CONTENT = 'script,style,link,iframe,object,embed,form,input,button,textarea,select,noscript,template,svg,canvas,video,audio';
  const OTHER_MODAL_SELECTOR = [
    '.dellumia-bogos-modal:not([hidden])',
    '.product-popup-modal[open]',
    'modal-dialog[open]',
    'dialog[open]',
    '[role="dialog"][aria-modal="true"]:not([hidden])'
  ].join(',');

  let modal, dialog, heading, content;
  let trigger = null;
  let ownerProduct = null;
  let currentURL = null;
  let anchorIds = new Map();
  let controller = null;
  let loadId = 0;
  let locked = null;
  let backgroundNodes = new Map();
  let nodeObserver = null;
  let observerFrame = 0;
  let resizeFrame = 0;
  let campaignReadinessObserver = null;
  let campaignReadinessSource = null;
  let campaignReadinessFrame = 0;
  let campaignReadinessRuns = 0;
  let campaignAssetFinalRuns = 0;
  let campaignReadinessReason = null;
  let campaignReadinessGeneration = 0;
  // Separate bounded budgets: early content mutations cannot use the final
  // measurements reserved for a delayed campaign font or image.
  const MAX_CAMPAIGN_MUTATION_RUNS = 10;
  const MAX_CAMPAIGN_ASSET_FINAL_RUNS = 2;

  function allowedURL(value, base = location.href) {
    try {
      const url = new URL(value, base);
      if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) return null;
      if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:pages|policies)\/[^/]+/i.test(url.pathname)) return null;
      return url;
    } catch (_) { return null; }
  }

  function unescapeHash(hash) {
    try { return decodeURIComponent((hash || '').replace(/^#/, '')); }
    catch (_) { return (hash || '').replace(/^#/, ''); }
  }

  function isOpen() { return Boolean(modal && !modal.hidden); }

  function findOtherModal() {
    for (const element of document.querySelectorAll(OTHER_MODAL_SELECTOR)) {
      if (element === dialog || modal?.contains(element) || element.closest('[hidden]')) continue;
      // Dazzle keeps the header search dialog in the DOM inside a closed
      // <details>. Its layout box may exist even when search is not open.
      // Do not treat a dialog inside a closed details element as active.
      if (element.closest('details:not([open])')) continue;
      if (!element.getClientRects().length) continue;
      const style = getComputedStyle(element);
      if (style.display !== 'none' && style.visibility !== 'hidden') return element;
    }
    return null;
  }

  function isOtherModalVisible() { return Boolean(findOtherModal()); }

  function focusReceivingModal(other) {
    if (!other?.isConnected || isOpen()) return;
    const receiver = other.matches('[role="dialog"]') ? other :
      (other.querySelector('[role="dialog"]') || other);
    if (other.contains(document.activeElement)) return;
    const first = [...receiver.querySelectorAll(
      'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
    )].find(el => el.getClientRects().length && !el.inert && !el.closest('[hidden]'));
    const target = receiver.hasAttribute('tabindex') ? receiver : (first || receiver);
    // The theme's modal dialog has tabindex=-1. Add a temporary focus target
    // only if a different modal implementation provides no focusable element.
    const temporary = !target.hasAttribute('tabindex') && target === receiver && !first;
    if (temporary) target.setAttribute('tabindex', '-1');
    try { target.focus({ preventScroll: true }); }
    finally { if (temporary) target.removeAttribute('tabindex'); }
  }

  function handOffTo(other) {
    // The receiving modal may have attempted its initial focus while the
    // background was still inert. Release it first, then retry focus next frame.
    closeModal(false);
    if (!other) return;
    requestAnimationFrame(() => {
      // A rapid modal switch can close the original receiver and open another
      // before this callback executes. Focus only the currently visible dialog.
      const currentReceiver = findOtherModal();
      if (currentReceiver) focusReceivingModal(currentReceiver);
    });
  }

  function setupModal() {
    if (modal) return;
    modal = document.createElement('div');
    modal.className = MODAL_CLASS;
    modal.hidden = true;
    modal.innerHTML = `
      <div class="dellumia-product-info-popup__overlay" data-dellumia-info-close aria-hidden="true"></div>
      <div class="dellumia-product-info-popup__dialog" role="dialog" aria-modal="true"
           aria-labelledby="DellumiaProductInfoPopupTitle" tabindex="-1">
        <button type="button" class="dellumia-product-info-popup__close" data-dellumia-info-close
                aria-label="Close information popup">
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M5 5L19 19M19 5L5 19" fill="none" stroke="currentColor"
                  stroke-width="1.6" stroke-linecap="round" />
          </svg>
        </button>
        <h2 class="dellumia-product-info-popup__heading" id="DellumiaProductInfoPopupTitle"
            tabindex="0"></h2>
        <div class="dellumia-product-info-popup__content rte" role="region"
             aria-label="Information content" aria-live="polite" tabindex="0"></div>
      </div>`;
    dialog = modal.querySelector('.dellumia-product-info-popup__dialog');
    heading = modal.querySelector('.dellumia-product-info-popup__heading');
    content = modal.querySelector('.dellumia-product-info-popup__content');
    modal.addEventListener('click', onModalClick);
    document.body.appendChild(modal);
  }

  function matchCampaign(product) {
    // Do not borrow variables from a different product/quick-view instance.
    const campaign = product?.querySelector(BLOCK_SELECTOR);
    const vars = [];
    for (let i = 0; i < modal.style.length; i += 1) {
      const property = modal.style.item(i);
      if (property.startsWith('--dbp-popup-')) vars.push(property);
    }
    vars.forEach((property) => modal.style.removeProperty(property));
    modal.style.removeProperty('font-family');
    if (!campaign) return; // Use DE LUXE CSS fallbacks if the block is absent.
    for (let i = 0; i < campaign.style.length; i += 1) {
      const property = campaign.style.item(i);
      if (property.startsWith('--dbp-popup-')) {
        modal.style.setProperty(property, campaign.style.getPropertyValue(property));
      }
    }
    modal.style.fontFamily = getComputedStyle(campaign).fontFamily;
  }

  function measureCampaignHeight(product) {
    // DE LUXE's dialog height depends on its own content and loaded assets.
    // Read a hidden inert copy; never open or change the real promo dialog.
    let probe = null;
    try {
      const source = product?.querySelector(BLOCK_SELECTOR);
      const original = source?.querySelector('.dellumia-bogos-modal__dialog');
      if (!original) {
        modal?.style.removeProperty('--dellumia-info-dialog-height');
        return false;
      }
      probe = document.createElement('div');
      probe.className = 'dellumia-product-info-popup__measure';
      probe.setAttribute('aria-hidden', 'true');
      probe.inert = true;
      const sample = original.cloneNode(true);
      sample.removeAttribute('role');
      sample.removeAttribute('aria-modal');
      sample.removeAttribute('aria-labelledby');
      sample.removeAttribute('tabindex');
      sample.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
      probe.appendChild(sample);
      for (let i = 0; i < source.style.length; i += 1) {
        const property = source.style.item(i);
        if (property.startsWith('--dbp-popup-')) {
          probe.style.setProperty(property, source.style.getPropertyValue(property));
        }
      }
      probe.style.fontFamily = getComputedStyle(source).fontFamily;
      document.body.appendChild(probe);
      const height = sample.getBoundingClientRect().height;
      if (!Number.isFinite(height) || height <= 80) {
        modal?.style.removeProperty('--dellumia-info-dialog-height');
        return false;
      }
      modal.style.setProperty('--dellumia-info-dialog-height', `${height}px`);
      return true;
    } catch (_) {
      // Measurement must never prevent a link from opening its popup.
      // The existing responsive CSS max-height handles this fallback.
      modal?.style.removeProperty('--dellumia-info-dialog-height');
      return false;
    } finally {
      probe?.remove();
    }
  }

  function stopCampaignReadiness() {
    campaignReadinessGeneration += 1;
    campaignReadinessObserver?.disconnect();
    campaignReadinessObserver = null;
    if (campaignReadinessSource) {
      campaignReadinessSource.removeEventListener('load', onCampaignAssetReady, true);
      campaignReadinessSource.removeEventListener('error', onCampaignAssetReady, true);
    }
    campaignReadinessSource = null;
    if (document.fonts?.removeEventListener) {
      document.fonts.removeEventListener('loadingdone', onCampaignAssetReady);
      document.fonts.removeEventListener('loadingerror', onCampaignAssetReady);
    }
    if (campaignReadinessFrame) cancelAnimationFrame(campaignReadinessFrame);
    campaignReadinessFrame = 0;
    campaignReadinessReason = null;
    campaignReadinessRuns = 0;
    campaignAssetFinalRuns = 0;
  }

  function hasPendingCampaignAssets() {
    if (document.fonts?.status === 'loading') return true;
    // A hidden lazy image might not begin loading until the actual promotional
    // dialog opens. Do not block readiness indefinitely for that case.
    return [...campaignReadinessSource.querySelectorAll(
      '.dellumia-bogos-modal__dialog img'
    )].some(img => !img.complete && (img.loading !== 'lazy' || img.getClientRects().length));
  }

  function isRelevantCampaignMutation(records) {
    const dialogSelector = '.dellumia-bogos-modal__dialog';
    return records.some((record) => {
      const node = record.target;
      // Inline design-variable changes on the owning block also affect the
      // campaign dialog height, unlike unrelated progress-widget updates.
      if (node === campaignReadinessSource && record.type === 'attributes' &&
          record.attributeName === 'style') return true;
      const owner = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      if (owner?.closest(dialogSelector)) return true;
      if (record.type !== 'childList') return false;
      // Also detect replacement of the dialog itself without watching or
      // measuring unrelated progress-bar updates elsewhere in the block.
      return [...record.addedNodes, ...record.removedNodes].some(el =>
        el.nodeType === Node.ELEMENT_NODE &&
        (el.matches?.(dialogSelector) || el.querySelector?.(dialogSelector))
      );
    });
  }

  function scheduleCampaignReadinessMeasure(reason = 'mutation') {
    if (!isOpen() || !ownerProduct?.isConnected || !campaignReadinessSource) return;
    const asset = reason === 'asset';
    if (asset) {
      if (campaignAssetFinalRuns >= MAX_CAMPAIGN_ASSET_FINAL_RUNS ||
          hasPendingCampaignAssets()) return;
    } else if (campaignReadinessRuns >= MAX_CAMPAIGN_MUTATION_RUNS) {
      // Once the budget is exhausted, the observer must not keep monitoring
      // an active BOGOS widget when it can no longer schedule work.
      campaignReadinessObserver?.disconnect();
      campaignReadinessObserver = null;
      return;
    }
    // Asset readiness takes priority if both events arrive in one frame.
    if (asset || !campaignReadinessReason) campaignReadinessReason = reason;
    if (campaignReadinessFrame) return;
    const generation = campaignReadinessGeneration;
    campaignReadinessFrame = requestAnimationFrame(() => {
      campaignReadinessFrame = 0;
      const pendingReason = campaignReadinessReason;
      campaignReadinessReason = null;
      if (generation !== campaignReadinessGeneration || !isOpen() ||
          !ownerProduct?.isConnected || !campaignReadinessSource) return;
      if (pendingReason === 'asset') {
        if (campaignAssetFinalRuns >= MAX_CAMPAIGN_ASSET_FINAL_RUNS ||
            hasPendingCampaignAssets()) return;
        campaignAssetFinalRuns += 1;
      } else {
        if (campaignReadinessRuns >= MAX_CAMPAIGN_MUTATION_RUNS) return;
        campaignReadinessRuns += 1;
      }
      measureCampaignHeight(ownerProduct);
      if (campaignReadinessRuns >= MAX_CAMPAIGN_MUTATION_RUNS) {
        campaignReadinessObserver?.disconnect();
        campaignReadinessObserver = null;
      }
      if (campaignAssetFinalRuns >= MAX_CAMPAIGN_ASSET_FINAL_RUNS) {
        // The asset completion budget is over. Keep only the separately
        // bounded mutation observer if it still has useful work available.
        campaignReadinessSource.removeEventListener('load', onCampaignAssetReady, true);
        campaignReadinessSource.removeEventListener('error', onCampaignAssetReady, true);
        if (document.fonts?.removeEventListener) {
          document.fonts.removeEventListener('loadingdone', onCampaignAssetReady);
          document.fonts.removeEventListener('loadingerror', onCampaignAssetReady);
        }
      }
    });
  }

  function onCampaignAssetReady(event) {
    // Ignore BOGOS progress icon loads; only assets inside the dialog
    // influence the dialog measurement.
    if (event?.target?.tagName === 'IMG' &&
        !event.target.closest('.dellumia-bogos-modal__dialog')) return;
    scheduleCampaignReadinessMeasure('asset');
  }

  function startCampaignReadiness(product) {
    stopCampaignReadiness();
    const source = product?.querySelector(BLOCK_SELECTOR);
    if (!source) return;
    campaignReadinessSource = source;
    const generation = campaignReadinessGeneration;
    campaignReadinessObserver = new MutationObserver((records) => {
      if (isRelevantCampaignMutation(records)) scheduleCampaignReadinessMeasure('mutation');
    });
    campaignReadinessObserver.observe(source, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['src', 'class', 'style', 'hidden']
    });
    // Capture load/error from campaign images, including those inserted later.
    source.addEventListener('load', onCampaignAssetReady, true);
    source.addEventListener('error', onCampaignAssetReady, true);
    if (document.fonts?.addEventListener) {
      document.fonts.addEventListener('loadingdone', onCampaignAssetReady);
      document.fonts.addEventListener('loadingerror', onCampaignAssetReady);
    }
    // A late font-ready event gets reserved measurement capacity even if
    // the earlier promotional mutations exhaust their separate budget.
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (generation === campaignReadinessGeneration)
          scheduleCampaignReadinessMeasure('asset');
      }).catch(() => { /* Keep last valid measurement or CSS fallback. */ });
    }
  }

  function applyInert() {
    if (!document.body) return;
    for (const node of Array.from(document.body.children)) {
      if (node === modal || backgroundNodes.has(node)) continue;
      backgroundNodes.set(node, node.inert);
      node.inert = true;
    }
  }

  function lockBackground() {
    const body = document.body;
    const root = document.documentElement;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const css = ['overflow','paddingRight','position','top','left','right','width'];
    const old = {};
    css.forEach((key) => { old[key] = { value: body.style[key], priority: body.style.getPropertyPriority(key.replace(/[A-Z]/g, m => '-' + m.toLowerCase())) }; });
    const width = Math.max(0, window.innerWidth - root.clientWidth);
    const smallScreen = window.matchMedia('(max-width: 749px)').matches;
    locked = { scrollX, scrollY, old, smallScreen };
    if (smallScreen) {
      body.style.position = 'fixed';
      body.style.top = `${-scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
    } else if (width > 0) {
      const existing = parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${existing + width}px`;
    }
    body.classList.add('dellumia-product-info-popup-open');
    applyInert();
    // Observe nested replacements and modal state changes, but inspect at
    // most once per animation frame even if apps update many DOM attributes.
    nodeObserver = new MutationObserver(() => {
      if (!isOpen()) return;
      if (!ownerProduct?.isConnected) { closeModal(false); return; }
      if (observerFrame) return;
      observerFrame = requestAnimationFrame(() => {
        observerFrame = 0;
        if (!isOpen()) return;
        if (!ownerProduct?.isConnected) { closeModal(false); return; }
        const other = findOtherModal();
        if (other) { handOffTo(other); return; }
        applyInert();
      });
    });
    nodeObserver.observe(body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'open', 'class', 'style', 'aria-hidden']
    });
  }

  function unlockBackground() {
    nodeObserver?.disconnect();
    nodeObserver = null;
    if (observerFrame) cancelAnimationFrame(observerFrame);
    observerFrame = 0;
    for (const [node, wasInert] of backgroundNodes) {
      if (node.isConnected) node.inert = wasInert;
    }
    backgroundNodes.clear();
    if (!locked) return;
    const body = document.body;
    body.classList.remove('dellumia-product-info-popup-open');
    for (const [prop, old] of Object.entries(locked.old)) {
      const property = prop.replace(/[A-Z]/g, m => '-' + m.toLowerCase());
      if (old.value) body.style.setProperty(property, old.value, old.priority);
      else body.style.removeProperty(property);
    }
    window.scrollTo(locked.scrollX, locked.scrollY);
    locked = null;
  }

  function isSafeURL(value, attr, baseURL) {
    try {
      const u = new URL(value, baseURL);
      if (attr === 'href') return ['https:', 'http:', 'mailto:', 'tel:'].includes(u.protocol);
      return ['https:', 'http:'].includes(u.protocol);
    } catch (_) { return false; }
  }

  function sanitize(fragment, url, id) {
    const clone = fragment.cloneNode(true);
    clone.querySelectorAll(NON_CONTENT).forEach(el => el.remove());
    const map = new Map();
    let counter = 0;
    const all = [clone, ...clone.querySelectorAll('*')];
    for (const el of all) {
      if (el.nodeType !== Node.ELEMENT_NODE) continue;
      // Legacy headings can use name= without id=; normalize these anchors
      // before the ID namespace rewrite, just as for <a name=...> anchors.
      const isNamedAnchor = el.tagName === 'A' || /^H[1-6]$/.test(el.tagName);
      const oldId = el.getAttribute('id') || (isNamedAnchor ? el.getAttribute('name') : '');
      for (const attribute of [...el.attributes]) {
        const name = attribute.name.toLowerCase();
        if (name.startsWith('on') || ['srcdoc','style','class','id','name','srcset','formaction','action'].includes(name)) {
          el.removeAttribute(attribute.name);
        }
      }
      if (oldId) {
        const safeId = `dellumia-info-${id}-${++counter}`;
        if (!map.has(oldId)) map.set(oldId, safeId);
        el.id = safeId;
      }
      for (const attr of ['href','src','poster']) {
        const val = el.getAttribute(attr);
        if (!val) continue;
        if (!isSafeURL(val, attr, url.href)) { el.removeAttribute(attr); continue; }
        el.setAttribute(attr, new URL(val, url.href).href);
      }
      if (el.tagName === 'A' && el.hasAttribute('href')) {
        // Links outside informational pages never navigate this tab.
        const dest = allowedURL(el.href, url.href);
        if (!dest) { el.target = '_blank'; el.rel = 'noopener noreferrer'; }
      }
    }
    // ID rewriting must also preserve relationships used by assistive technology.
    // Remove references to elements excluded from the static informational view.
    const idReferenceAttributes = [
      'aria-labelledby', 'aria-describedby', 'aria-details', 'aria-controls',
      'aria-owns', 'aria-flowto', 'aria-errormessage', 'aria-activedescendant',
      'headers', 'for', 'list', 'itemref'
    ];
    for (const element of all) {
      if (element.nodeType !== Node.ELEMENT_NODE) continue;
      for (const attr of idReferenceAttributes) {
        const value = element.getAttribute(attr);
        if (!value) continue;
        const remapped = value.trim().split(/\s+/).map(token => map.get(token)).filter(Boolean);
        if (remapped.length) element.setAttribute(attr, remapped.join(' '));
        else element.removeAttribute(attr);
      }
    }
    return { clone, map };
  }

  function pageData(html, url, id) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const main = parsed.querySelector('main#MainContent') || parsed.querySelector('main');
    if (!main) throw new Error('Main content not found');
    const policy = main.querySelector('.shopify-policy__body');
    const titleNode = main.querySelector('.shopify-policy__title h1, .shopify-policy__title, section .container > h1, .page-title, h1');
    const pageTitle = titleNode?.textContent.trim() || parsed.title.split(/[|–]/)[0].trim() || 'Information';
    const titleAnchors = [];
    if (titleNode) {
      if (titleNode.id) titleAnchors.push(titleNode.id);
      if (titleNode.getAttribute('name')) titleAnchors.push(titleNode.getAttribute('name'));
      titleNode.querySelectorAll('[id],a[name]').forEach(el => {
        if (el.id) titleAnchors.push(el.id);
        if (el.getAttribute('name')) titleAnchors.push(el.getAttribute('name'));
      });
    }
    const container = parsed.createElement('div');
    if (policy) {
      container.appendChild(policy.cloneNode(true));
    } else {
      // Explicit opt-in means the page author identified the complete content.
      const marked = [...main.querySelectorAll('[data-dellumia-popup-content]')]
        .filter(el => !el.parentElement?.closest('[data-dellumia-popup-content]'));
      if (marked.length) {
        marked.forEach(el => container.appendChild(el.cloneNode(true)));
      } else {
        // Only known Shopify Dazzle page sections are supported automatically.
        const sections = [...main.querySelectorAll('section .container, section .page-width--narrow')]
          .filter(el => el.querySelector(':scope > .rte') &&
            el.querySelector(':scope > h1, :scope > h2.page-title'));
        if (!sections.length) throw new Error('Unsupported informational page layout');

        // Fail closed when other informational sections also exist. Showing
        // just the familiar page RTE would silently omit important information.
        const selectedRichText = sections.map(el => el.querySelector(':scope > .rte'));
        const unexpectedRichText = [...main.querySelectorAll('.rte')].some(el =>
          !selectedRichText.some(selected => selected.contains(el))
        );
        const sectionRoots = [...main.querySelectorAll('[id^="shopify-section-"]')]
          .filter(el => !el.parentElement?.closest('[id^="shopify-section-"]'));
        const otherSections = sectionRoots.some(root => {
          const remainder = root.cloneNode(true);
          // Remove only recognized article blocks; any additional visible
          // information in the same or another section is an ambiguity.
          [...remainder.querySelectorAll('section .container, section .page-width--narrow')]
            .filter(el => el.querySelector(':scope > .rte') &&
              el.querySelector(':scope > h1, :scope > h2.page-title'))
            .forEach(el => el.remove());
          remainder.querySelectorAll('script,style,template,noscript').forEach(el => el.remove());
          return Boolean(remainder.textContent.trim() || remainder.querySelector('img,video,picture'));
        });
        if (unexpectedRichText || otherSections) {
          throw new Error('Ambiguous page contains additional information sections');
        }

        for (const section of sections) {
          const sectionHeading = section.querySelector(':scope > h1, :scope > h2.page-title');
          if (sectionHeading === titleNode) {
            // Heading stays visible in the dialog chrome; its anchors are
            // mapped to the top of the popup instead of being discarded.
          } else if (sectionHeading?.textContent.trim()) {
            const h2 = parsed.createElement('h2');
            // Preserve the source heading ID and named anchor before sanitize()
            // rewrites IDs into the popup's isolated namespace.
            for (const attr of ['id', 'name']) {
              if (sectionHeading.hasAttribute(attr))
                h2.setAttribute(attr, sectionHeading.getAttribute(attr));
            }
            [...sectionHeading.childNodes].forEach(node => h2.appendChild(node.cloneNode(true)));
            container.appendChild(h2);
          }
          container.appendChild(section.querySelector(':scope > .rte').cloneNode(true));
        }
      }
    }
    if (!container.textContent.trim() && !container.querySelector('img'))
      throw new Error('No static information found');
    const cleaned = sanitize(container, url, id);
    // The page heading is outside the sanitized body. Preserve its anchors.
    for (const anchor of titleAnchors) {
      if (!cleaned.map.has(anchor)) cleaned.map.set(anchor, '__popup_heading__');
    }
    return { title: pageTitle, ...cleaned };
  }

  function scrollToFragment(hash) {
    const name = unescapeHash(hash);
    const mapped = anchorIds.get(name);
    if (mapped === '__popup_heading__') { content.scrollTop = 0; return true; }
    const target = mapped ? content.querySelector(`#${CSS.escape(mapped)}`) : null;
    if (!target) return false;
    const offset = target.getBoundingClientRect().top - content.getBoundingClientRect().top;
    content.scrollTop = Math.max(0, content.scrollTop + offset - 12);
    return true;
  }

  async function loadPage(url) {
    controller?.abort();
    const active = ++loadId;
    controller = new AbortController();
    currentURL = url;
    anchorIds = new Map();
    heading.textContent = 'Information';
    content.replaceChildren();
    content.setAttribute('aria-busy', 'true');
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.textContent = 'Loading information…';
    content.appendChild(status);
    content.scrollTop = 0;
    try {
      const response = await fetch(url.href, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: controller.signal
      });
      if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) throw new Error('Invalid response');
      const destination = allowedURL(response.url);
      if (!destination) throw new Error('Page redirected outside allowed content');
      const html = await response.text();
      const data = pageData(html, destination, active);
      if (active !== loadId || !isOpen()) return;
      currentURL = destination;
      anchorIds = data.map;
      heading.textContent = data.title;
      // Replace the status node; do not leave "Loading information…" behind.
      content.replaceChildren(...Array.from(data.clone.childNodes));
      if (url.hash) {
        requestAnimationFrame(() => {
          if (active === loadId && isOpen()) scrollToFragment(url.hash);
        });
      }
    } catch (err) {
      if (err.name === 'AbortError' || active !== loadId || !isOpen()) return;
      heading.textContent = 'Information';
      const message = document.createElement('p');
      message.textContent = 'Sorry, this information could not be loaded.';
      const fallback = document.createElement('a');
      fallback.href = url.href;
      fallback.target = '_blank';
      fallback.rel = 'noopener noreferrer';
      fallback.textContent = 'Open this page in a new tab';
      content.replaceChildren(message, fallback);
    } finally {
      if (active === loadId) content.removeAttribute('aria-busy');
    }
  }

  function openModal(url, link) {
    if (isOpen() || isOtherModalVisible()) return false;
    setupModal();
    ownerProduct = link.closest('product-info');
    matchCampaign(ownerProduct);
    measureCampaignHeight(ownerProduct);
    trigger = link;
    modal.hidden = false;
    lockBackground();
    startCampaignReadiness(ownerProduct);
    loadPage(url);
    dialog.focus({ preventScroll: true });
    return true;
  }

  function closeModal(restoreFocus = true) {
    if (!isOpen()) return;
    loadId += 1;
    controller?.abort();
    stopCampaignReadiness();
    if (resizeFrame) cancelAnimationFrame(resizeFrame);
    resizeFrame = 0;
    modal.hidden = true;
    unlockBackground();
    const toFocus = trigger;
    trigger = null;
    ownerProduct = null;
    currentURL = null;
    anchorIds.clear();
    if (restoreFocus && toFocus?.isConnected) toFocus.focus({ preventScroll: true });
  }

  function onModalClick(event) {
    const close = event.target.closest?.('[data-dellumia-info-close]');
    if (close) { event.preventDefault(); closeModal(); return; }
    const link = event.target.closest?.('.dellumia-product-info-popup__content a[href]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey ||
        event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download')) return;
    const resolved = new URL(link.getAttribute('href'), currentURL?.href || location.href);
    const url = allowedURL(resolved.href, currentURL?.href || location.href);
    if (!url) return; // Already marked to open in a new tab.
    event.preventDefault();
    if (currentURL && url.pathname === currentURL.pathname && url.search === currentURL.search) {
      if (url.hash) scrollToFragment(url.hash);
      else content.scrollTop = 0;
      return;
    }
    loadPage(url);
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey ||
        event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.(LINK_SELECTOR);
    if (!link || link.hasAttribute('download') || !document.contains(link) || isOpen()) return;
    const url = allowedURL(link.getAttribute('href'));
    if (!url || isOtherModalVisible()) return;
    event.preventDefault();
    openModal(url, link);
  });

  document.addEventListener('focusin', (event) => {
    if (!isOpen() || modal.contains(event.target)) return;
    if (!ownerProduct?.isConnected) { closeModal(false); return; }
    const other = findOtherModal();
    if (other) { handOffTo(other); return; }
    dialog.focus({ preventScroll: true });
  }, true);

  document.addEventListener('keydown', (event) => {
    if (!isOpen()) return;
    if (!ownerProduct?.isConnected) { closeModal(false); return; }
    const other = findOtherModal();
    if (other) { handOffTo(other); return; }
    if (event.key === 'Escape') { event.preventDefault(); closeModal(); return; }
    if (event.key !== 'Tab') return;
    const candidates = [...dialog.querySelectorAll(
      'a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])'
    )].filter(el => !el.closest('[hidden]') && el.getClientRects().length && !el.inert);
    if (!candidates.length) { event.preventDefault(); dialog.focus(); return; }
    const first = candidates[0], last = candidates[candidates.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog || !dialog.contains(document.activeElement))) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement) || document.activeElement === dialog)) {
      event.preventDefault(); first.focus();
    }
  });

  window.addEventListener('resize', () => {
    if (!isOpen() || resizeFrame) return;
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      if (isOpen() && ownerProduct?.isConnected) measureCampaignHeight(ownerProduct);
    });
  }, { passive: true });

  document.addEventListener('shopify:section:unload', (event) => {
    if (isOpen() && ownerProduct && (event.target.contains(ownerProduct) || !ownerProduct.isConnected)) closeModal(false);
  });
})();
